///
/// EarthBrowser
///
/// Created by Matt Giger on 12/02/2025
/// Copyright (c) 2025 Geodata Labs
///

import { Constants } from "@/constants"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { spherical } from "@/earthbrowser/utils/spherical"
import { engineActions, engineBridge } from "@/earthbrowser/engineBridge"
import type { Camera } from "@stovokor/camera/Camera"
import { CameraState } from "@stovokor/camera/CameraState"
import { type Perspective } from "@stovokor/camera/Perspective"
import { clamp, cross, dot, normal, quaternion, range, ray, sphere, spring, trig, vec3 } from "@stovokor/math"

const cMovementSpringDamping = 30
const cZoomSpringDamping = 15

/**
 * 2D/3D tilt toggle ("crane" move). Softer than the gesture springs so the
 * camera reads as rising and pitching over the subject rather than snapping;
 * softer still when a lot of tilt is being removed (steep oblique to top-down)
 */
const cCraneSpringDamping = 14
const cCraneSlowSpringDamping = 11
const cCraneSlowThresholdTilt = 50 * trig.rad

/**
 * Top-down pivot range cap, in camera altitudes down-range of the camera's
 * ground point. At oblique angles the screen-center hit runs off toward the
 * horizon (or misses the Earth entirely) while the subject the user is
 * reading is the foreground, so the pivot is pulled back to this range
 */
const cCranePivotRangeAltitudes = 2.5

/**
 * Range cap for ground-locked drags and zooms (pan, wheel, pinch), in camera
 * altitudes: grabbing the far ground near the horizon would otherwise move
 * the globe hundreds of km per pixel. Looser than the crane pivot since it
 * is a runaway guard, not a destination
 */
const cDragRangeAltitudes = 4
/** And never closer to the limb than this fraction of the way (high altitude) */
const cDragLimbFraction = 0.85

/**
 * Who is driving the camera: a pointer / touch / wheel gesture ("user") or
 * an automated move - fly-to, zoom buttons, crane, orbit, tour ("auto").
 * Gesture handlers pass "user" on their writes; everything else defaults
 * to "auto". The crosshair reads this: it marks where a gesture is taking
 * the camera, and automated motion has nothing for the user to aim
 */
export type CameraMotionSource = "user" | "auto"

/** Axis/angle rotation taking unit vector `from` onto unit vector `to` */
function rotationBetween(from: vec3, to: vec3): { axis: vec3; angle: number } {
	const axis = cross(from, to)
	if (axis.length < 1e-9) return { axis: vec3.unitY, angle: 0 }
	return { axis: normal(axis), angle: Math.acos(clamp(dot(from, to), -1, 1)) }
}

/** Shift `angle` by whole turns so it is within half a turn of `reference` */
function unwrapNear(angle: number, reference: number): number {
	let a = angle
	while (a - reference > trig.pi) a -= trig.two_pi
	while (a - reference < -trig.pi) a += trig.two_pi
	return a
}

/** Camera position plus its orthonormal forward / up / right frame */
export interface CameraBasis {
	position: vec3
	forward: vec3
	up: vec3
	right: vec3
}

/**
 * Anchor for a ground-locked gesture: the camera pose when it began and the
 * ground point that was under the pointer in that pose. `hit` is false when
 * the pointer was off the Earth (space), in which case callers fall back to
 * a centre-based motion
 */
export interface GroundDragAnchor {
	params: CameraParams
	basis: CameraBasis
	ground: vec3
	hit: boolean
}

/**
 * Camera parameters for animation
 */
export class CameraParams {
	radius: number
	theta: number
	phi: number
	tilt: number
	bearing: number

	constructor(radius: number | spherical, theta?: number, phi?: number, tilt?: number, bearing?: number) {
		if (radius instanceof spherical) {
			this.radius = radius.radius
			this.theta = radius.theta
			this.phi = radius.phi
			this.tilt = theta ?? 0
			this.bearing = phi ?? 0
		} else {
			this.radius = radius
			this.theta = theta ?? 0
			this.phi = phi ?? 0
			this.tilt = tilt ?? 0
			this.bearing = bearing ?? 0
		}
	}

	get array(): number[] {
		return [this.radius, this.theta, this.phi, this.tilt, this.bearing]
	}
}

/**
 * Optional movement constraint for restricted contexts (embeds): keeps the
 * camera inside a spherical cap around a center direction and inside an
 * altitude band. Tilt and bearing stay free (the horizon clamp already
 * bounds them), so the viewer can play with the point of view without
 * wandering away from the embedded subject.
 */
export interface CameraConstraint {
	/** Unit direction from the Earth's center to the constraint center */
	center: vec3
	/** Maximum great-circle angle (radians) the camera may wander from center */
	maxArc: number
	/** Allowed camera distance from the Earth's center (km) */
	radiusRange: range
}

/**
 * A rigid camera frame captured at the start of a fulcrum-centered gesture.
 * Holds the pivot point and the camera basis so the motion can be expressed
 * as a single rotation about the fulcrum, independent of the live (animating)
 * camera. This keeps the fulcrum stable on screen during the gesture.
 */
export interface FulcrumOrbitAnchor {
	/** The pivot point on the Earth that should remain fixed on screen */
	fulcrum: vec3
	/** Camera position relative to the fulcrum (cameraPos - fulcrum) */
	offset: vec3
	forward: vec3
	right: vec3
	up: vec3
}

/**
 * EarthCameraState - Manages camera state and animation for Earth viewing
 *
 * This class encapsulates:
 * - Camera position in spherical coordinates (radius, theta, phi)
 * - Camera orientation (tilt, bearing)
 * - Spring animation for smooth transitions
 * - Projection updates
 *
 * Extracted from EarthInteraction to be used by Earth GestureNode and its handlers.
 */
export class EarthCameraState {
	public context: EarthBrowserContext

	/** Current camera reference */
	public camera?: Camera

	/** Camera position in spherical coordinates relative to Earth */
	public spherical = new spherical(Constants.cMaxCameraAltitude + wgs.a, 0, trig.half_pi)

	/** Camera tilt angle (0 = looking at Earth center, π/2 = looking at horizon) */
	public tilt = 0

	/** Camera bearing/heading angle (rotation around the look axis) */
	public bearing = 0

	/** Valid elevation range for camera */
	public readonly elevationRange = new range(
		Constants.cMinCameraAltitude + wgs.a,
		Constants.cMaxCameraAltitude + wgs.a
	)

	/** Spring animation controller */
	private spring: spring

	/**
	 * 2D/3D crane move: a one-value spring (progress 0 to 1) driving a pose
	 * function, so the camera follows the arc about its pivot instead of
	 * springing radius/theta/phi/tilt/bearing independently (which lets the
	 * subject drift across the screen mid-move). A fresh spring per move so
	 * no velocity leaks in from an interrupted one
	 */
	private craneSpring?: spring
	private cranePose?: (progress: number) => CameraParams | undefined

	/**
	 * True while the parameter spring is carrying a released drag (a glide)
	 * rather than tracking input or a programmatic move: a finger or mouse
	 * landing on the globe catches a glide where it is, but must not
	 * interrupt a fly-to
	 */
	private gliding = false

	/** Source of the motion in flight (or the last one); see CameraMotionSource */
	private source: CameraMotionSource = "auto"

	/** Optional movement constraint (embeds); applied to every motion target */
	private constraint?: CameraConstraint

	/** First activation flag for initial setup */
	private firstActivation = true

	/** Flag to prevent feedback loop when updating from user input */
	private skipNextReduxUpdate = false

	constructor(context: EarthBrowserContext) {
		this.context = context

		// Set up spring animation
		this.spring = new spring()
			.damping(cMovementSpringDamping)
			.tolerance([1, 0.001, 0.001, 0.001, 0.001, 0.001])
			.onUpdate(this.onSpringUpdate.bind(this))
			.onComplete(this.onSpringComplete.bind(this))
	}

	// ========== Camera Control ==========

	/**
	 * Called when Earth gains focus - set up camera
	 */
	public activate(camera: Camera): void {
		this.camera = camera
		this.stopAnimations()
		camera.state.stopAnimating()

		if (this.firstActivation) {
			this.firstActivation = false

			// Restore from saved state or use defaults (no store in embed
			// builds: the default pose applies, then the embed's URL params)
			const state = engineBridge.getState()?.earthbrowser.cameraState
			if (state) {
				this.spherical = new spherical(state.radius, state.theta, state.phi)
				this.tilt = state.tilt
				this.bearing = state.bearing
			} else {
				const defaultRadius = Constants.cDefaultCameraAltitude + wgs.a
				this.spherical = new spherical(defaultRadius, 0, trig.half_pi)
				this.tilt = 0
				this.bearing = 0
			}
			this.updateProjection()
		} else {
			// Sync our internal state from current camera position/orientation
			// syncFromCamera() will preserve the exact tilt/bearing without clamping
			this.syncFromCamera()
			this.saveCameraState()
			this.updateProjection()
		}
	}

	/**
	 * Called when Earth loses focus
	 */
	public deactivate(): void {
		this.stopAnimations()
		this.camera?.state.stopAnimating()
	}

	/** Who started the motion in flight (or the last one) */
	public get motionSource(): CameraMotionSource {
		return this.source
	}

	/** Stop the parameter spring and any crane move in flight */
	private stopAnimations(): void {
		this.spring.stop()
		this.gliding = false
		this.stopCrane()
	}

	private stopCrane(): void {
		this.craneSpring?.stop()
		this.craneSpring = undefined
		this.cranePose = undefined
	}

	/**
	 * Save current camera state to Redux
	 */
	public saveCameraState(): void {
		// Skip Redux update if we're responding to user slider input
		// This prevents feedback loops
		if (this.skipNextReduxUpdate) {
			this.skipNextReduxUpdate = false
			return
		}

		// Validate tilt and bearing before saving to prevent invalid states
		// This is especially important after syncing from camera when transitioning
		// from other focuses (e.g., satellites) where the camera might be pointing
		// away from Earth
		const maxAllowedTilt = this.maxTilt()
		const validatedTilt = clamp(this.tilt, 0, maxAllowedTilt)
		const validatedBearing = this.clipBearing(this.bearing)

		// Update internal state to match validated values to keep consistency
		this.tilt = validatedTilt
		this.bearing = validatedBearing

		engineBridge.dispatch({
			type: engineActions.setCameraState,
			payload: {
				radius: this.spherical.radius,
				theta: this.spherical.theta,
				phi: this.spherical.phi,
				tilt: validatedTilt,
				bearing: validatedBearing
			}
		})
	}

	/**
	 * Get the target camera state for focus transitions
	 * Returns the camera position/orientation based on current spherical coords, tilt, and bearing
	 */
	public getTargetCameraState(): CameraState {
		const position = this.spherical.cartesian
		const toEarthCenter = normal(position).multiply(-1)

		// Create initial coordinate system looking at Earth center
		// (avoid pole singularity where toEarthCenter is parallel to unitY)
		const { up: initialUp } = EarthCameraState.initialRightUp(toEarthCenter)

		// Apply bearing: rotate the up vector around the toEarthCenter axis
		const bearingQuat = quaternion.fromAngleAxis(this.bearing, toEarthCenter)
		const rotatedUp = bearingQuat.rotateVector(initialUp)
		const rotatedRight = normal(cross(toEarthCenter, rotatedUp))

		// Apply tilt: tilt away from Earth center
		const tiltQuat = quaternion.fromAngleAxis(this.tilt, rotatedRight)
		const forward = tiltQuat.rotateVector(toEarthCenter)
		const up = tiltQuat.rotateVector(rotatedUp)

		return new CameraState(position, forward, up)
	}

	/**
	 * Get current camera parameters
	 */
	public getCurrentParams(): CameraParams {
		if (this.spring.running) {
			const current = this.spring.current
			return new CameraParams(current[0], current[1], current[2], current[3], current[4])
		}
		return new CameraParams(this.spherical, this.tilt, this.bearing)
	}

	public animatedTiltAndBearing(tilt: number, bearing: number): void {
		const newTilt = clamp(tilt, 0, this.maxTilt() - 0.001)
		const newBearing = this.clipBearing(bearing)
		const newParams = new CameraParams(this.spherical, newTilt, newBearing)
		this.animateTo(newParams)
	}

	public animatedCameraBearing(bearing: number): void {
		const newBearing = this.clipBearing(bearing)
		const newParams = new CameraParams(this.spherical, this.tilt, newBearing)
		this.animateTo(newParams)
	}

	/**
	 * Animate to a new tilt value as a crane move: the camera swings about a
	 * pivot on the ground, keeping its distance to it, so the subject stays
	 * the same apparent size and (for the general case) fixed on screen.
	 *
	 * Tilt > 0: rigid rotation about the screen-center fulcrum, the same model
	 * the tilt gesture uses, played along its arc.
	 *
	 * Tilt = 0 (top-down): the camera rises to directly above the pivot at an
	 * altitude equal to its current slant distance to it, so the view does
	 * not zoom in. The pivot is the screen-center hit, pulled back toward the
	 * camera's ground point when that hit is far down-range or off the Earth
	 * (see cCranePivotRangeAltitudes). Preserving distance also makes 2D and
	 * 3D exact inverses, where keeping altitude used to zoom in ~30% per
	 * round trip.
	 */
	public animatedCameraTilt(targetTilt: number): boolean {
		if (!this.camera) return false

		const clampedTargetTilt = clamp(targetTilt, 0, this.maxTilt() - 0.001)
		const tiltChange = Math.abs(clampedTargetTilt - this.tilt)
		const damping = tiltChange > cCraneSlowThresholdTilt ? cCraneSlowSpringDamping : cCraneSpringDamping

		if (clampedTargetTilt < 0.01) {
			const pose = this.topDownCranePose()
			if (pose) this.runCrane(pose, damping)
			return true
		}

		const anchor = this.captureFulcrumAnchor()
		if (!anchor) return false

		const tiltDelta = clampedTargetTilt - this.tilt
		this.runCrane(progress => this.orbitAboutFulcrum(anchor, 0, tiltDelta * progress), damping)
		return true
	}

	/**
	 * Pose function for the top-down crane. Two rotations about the camera's
	 * right axis, in step: the position swings about the pivot from its
	 * start direction up to the pivot's surface normal, and the gaze turns
	 * from the start forward to straight down. When the pivot is the
	 * screen-center hit these are one rigid rotation and the pivot stays
	 * fixed on screen; when the pivot was pulled back the gaze turns a bit
	 * further and the screen center eases from the far hit onto the pivot.
	 * Undefined when the camera is already top-down over its pivot.
	 */
	private topDownCranePose(): ((progress: number) => CameraParams) | undefined {
		const camera = this.camera
		if (!camera) return undefined

		const position = camera.position
		const forward = camera.state.forward
		const up = camera.state.up

		// Screen-centre hit, capped in range (same sphere as the fulcrum anchor)
		const pivot = EarthCameraState.cappedGroundPoint(position, forward, cCranePivotRangeAltitudes, wgs.a + 2)
		const pivotNormal = normal(pivot)
		const offset = position.subtract(pivot)
		const swing = rotationBetween(normal(offset), pivotNormal)
		const gaze = rotationBetween(forward, pivotNormal.multiply(-1))
		if (swing.angle < 1e-4 && gaze.angle < 1e-4) return undefined

		return (progress: number) => {
			const t = clamp(progress, 0, 1)
			const qSwing = quaternion.fromAngleAxis(swing.angle * t, swing.axis)
			const qGaze = quaternion.fromAngleAxis(gaze.angle * t, gaze.axis)

			const newPosition = pivot.add(qSwing.rotateVector(offset))
			const newForward = qGaze.rotateVector(forward)
			const newUp = qGaze.rotateVector(up)

			const sph = new spherical(newPosition)
			const radius = this.elevationRange.clamp(sph.radius)
			const { tilt, bearing } = this.tiltBearingFromBasis(newPosition, newForward, newUp, radius)
			// Land exactly top-down rather than on numerical noise
			return this.createParams(radius, sph.theta, sph.phi, t >= 1 ? 0 : tilt, bearing)
		}
	}

	/**
	 * Run a crane move: progress springs 0 to 1 and the pose function is
	 * applied every frame. Replaces any motion in flight.
	 */
	private runCrane(pose: (progress: number) => CameraParams | undefined, damping: number): void {
		this.stopAnimations()
		this.source = "auto"
		this.cranePose = pose
		this.craneSpring = new spring()
			.damping(damping)
			.tolerance([0.002])
			.onUpdate(values => {
				const params = this.cranePose?.(values[0])
				if (params) this.applyParams(params)
			})
			.onComplete(() => {
				const params = this.cranePose?.(1)
				this.cranePose = undefined
				this.craneSpring = undefined
				if (params) this.applyParams(params)
				this.onSpringComplete()
			})
		this.craneSpring.from([0]).sendTo([1])
	}

	/**
	 * Immediately set tilt without animation (for direct user input like sliders)
	 * Simply updates the tilt value and lets the projection update on next frame
	 * Skips Redux update to prevent feedback loop
	 */
	public immediateCameraTilt(targetTilt: number): void {
		const clampedTilt = clamp(targetTilt, 0, this.maxTilt() - 0.001)
		this.tilt = clampedTilt
		this.stopAnimations()
		this.source = "auto"
		this.skipNextReduxUpdate = true
		this.updateProjection()
	}

	/**
	 * Immediately set bearing without animation (for direct user input like sliders)
	 * Skips Redux update to prevent feedback loop
	 */
	public immediateCameraBearing(bearing: number): void {
		this.bearing = this.clipBearing(bearing)
		this.stopAnimations()
		this.source = "auto"
		this.skipNextReduxUpdate = true
		this.updateProjection()
	}

	/**
	 * Immediately apply full camera parameters without the spring.
	 * Used for direct manipulation gestures (e.g. fulcrum orbit) so the camera
	 * tracks the pointer exactly instead of lagging/floating behind the spring,
	 * and by automated movers (orbit, tours) that pace their own poses.
	 * Redux is not updated here; callers persist state on gesture end.
	 */
	public setParamsImmediate(params: CameraParams, source: CameraMotionSource = "auto"): void {
		this.stopAnimations()
		this.source = source
		this.applyParams(params)
	}

	/** Adopt params (constrained) and refresh the projection; no spring bookkeeping */
	private applyParams(params: CameraParams): void {
		const constrained = this.constrainParams(params)
		this.spherical = new spherical(constrained.radius, constrained.theta, constrained.phi)
		this.tilt = constrained.tilt
		this.bearing = constrained.bearing
		this.updateProjection()
	}

	/**
	 * Install (or remove, with undefined) a movement constraint. Every
	 * subsequent motion target - gesture pans, zooms, double-tap zoom,
	 * fly-tos - is clamped to it; the current pose is left alone, so install
	 * the constraint after placing the camera at its home pose.
	 */
	public setConstraint(constraint?: CameraConstraint): void {
		this.constraint = constraint
	}

	/**
	 * Clamp target camera params to the active constraint: radius into the
	 * band, and the camera direction pulled back along the great circle to
	 * the cap edge when it strays past maxArc. Tilt and bearing pass
	 * through. Returns the params unchanged when no constraint is set.
	 */
	private constrainParams(params: CameraParams): CameraParams {
		const constraint = this.constraint
		if (!constraint) return params

		const radius = constraint.radiusRange.clamp(params.radius)
		const dir = normal(new spherical(1, params.theta, params.phi).cartesian)
		const angle = Math.acos(clamp(dot(dir, constraint.center), -1, 1))
		const sinAngle = Math.sin(angle)
		if (angle <= constraint.maxArc || sinAngle < 1e-6) {
			if (radius === params.radius) return params
			return new CameraParams(radius, params.theta, params.phi, params.tilt, params.bearing)
		}

		// Slerp from center toward the target direction, stopping at the cap edge
		const t = constraint.maxArc / angle
		const edgeDir = constraint.center.multiply(Math.sin((1 - t) * angle) / sinAngle)
			.add(dir.multiply(Math.sin(t * angle) / sinAngle))
		const edge = new spherical(edgeDir)

		// Unwrap theta next to the requested value so springs stay continuous
		let theta = edge.theta
		while (params.theta - theta > trig.pi) theta += trig.two_pi
		while (params.theta - theta < -trig.pi) theta -= trig.two_pi

		return new CameraParams(radius, theta, edge.phi, params.tilt, params.bearing)
	}

	/**
	 * Create new camera parameters
	 */
	public createParams(radius: number, theta: number, phi: number, tilt: number, bearing: number): CameraParams {
		return new CameraParams(radius, theta, phi, tilt, bearing)
	}

	/**
	 * The fulcrum (the point the camera is looking at: where the forward
	 * ray meets the Earth) for an arbitrary pose. Pure and static so
	 * React-side code that only has persisted camera params can use it -
	 * e.g. saved-view auto-naming, which should name the place in view,
	 * not the place under the camera (they differ when tilted).
	 */
	public static fulcrumForParams(params: CameraParams): vec3 {
		const { position, forward } = EarthCameraState.basisForParams(params)
		return new sphere(wgs.a + 2).closestPoint(new ray(position, forward))
	}

	/**
	 * Pose at `radius` with the given tilt and bearing whose fulcrum is the
	 * ground point `target` (so it lands at screen center). Top-down this is
	 * the camera straight over the point; tilted, the camera sits behind it
	 * along the bearing, solved by iterating the rotation that carries the
	 * current fulcrum onto the target (three passes are plenty; each is a
	 * second-order correction). Theta is unwrapped next to `thetaReference`
	 * so a spring or lerp toward the result never takes the long way round
	 */
	public static paramsLookingAt(target: vec3, radius: number, tilt: number, bearing: number, thetaReference?: number): CameraParams {
		const targetDir = normal(target)
		const over = new spherical(targetDir.multiply(radius))
		let params = new CameraParams(radius, over.theta, over.phi, tilt, bearing)
		if (tilt > 1e-5) {
			// Aim at the sphere through the target itself, not the generic
			// fulcrum sphere: close in and tilted, a 2 km height difference
			// is a visible offset on screen
			const aimSphere = new sphere(target.length)
			for (let pass = 0; pass < 3; pass++) {
				const basis = EarthCameraState.basisForParams(params)
				const fulcrum = aimSphere.closestPoint(new ray(basis.position, basis.forward))
				const { axis, angle } = rotationBetween(normal(fulcrum), targetDir)
				if (angle < 1e-9) break
				const position = quaternion.fromAngleAxis(angle, axis)
					.rotateVector(new spherical(params.radius, params.theta, params.phi).cartesian)
				const sph = new spherical(position)
				params = new CameraParams(radius, sph.theta, sph.phi, tilt, bearing)
			}
		}
		if (thetaReference !== undefined) {
			params = new CameraParams(radius, unwrapNear(params.theta, thetaReference), params.phi, tilt, bearing)
		}
		return params
	}

	/** The camera frame a pose resolves to (same construction as updateProjection) */
	public static basisForParams(params: CameraParams): CameraBasis {
		const position = new spherical(params.radius, params.theta, params.phi).cartesian
		const toEarthCenter = normal(position).multiply(-1)

		const { up: initialUp } = EarthCameraState.initialRightUp(toEarthCenter)
		const bearingQuat = quaternion.fromAngleAxis(params.bearing, toEarthCenter)
		const rotatedUp = bearingQuat.rotateVector(initialUp)
		const rotatedRight = normal(cross(toEarthCenter, rotatedUp))
		const tiltQuat = quaternion.fromAngleAxis(params.tilt, rotatedRight)
		const forward = tiltQuat.rotateVector(toEarthCenter)
		const up = tiltQuat.rotateVector(rotatedUp)

		return { position, forward, up, right: normal(cross(forward, up)) }
	}

	/**
	 * Where a ray from the camera lands on the ground sphere, with its range
	 * capped: no further down-range of the camera's ground point than
	 * rangeAltitudes times the altitude, nor past cDragLimbFraction of the
	 * way to the limb. Beyond the cap (or when the ray misses the Earth) the
	 * point is walked back along the ray's heading, so it is continuous
	 * across the cap and keeps a sky-pointing drag from running away
	 */
	public static cappedGroundPoint(position: vec3, direction: vec3, rangeAltitudes: number, groundRadius = wgs.a): vec3 {
		const radial = normal(position)
		const distance = position.length
		const altitude = distance - wgs.a
		const hit = new sphere(groundRadius).closestPoint(new ray(position, direction))
		const hitArc = Math.acos(clamp(dot(radial, normal(hit)), -1, 1))
		const limbArc = Math.acos(clamp(groundRadius / distance, -1, 1))
		const maxArc = Math.min(rangeAltitudes * altitude / groundRadius, cDragLimbFraction * limbArc)
		if (hitArc <= maxArc) return hit

		const heading = direction.subtract(radial.multiply(dot(direction, radial)))
		if (heading.length < 1e-6) return radial.multiply(groundRadius)
		return radial.multiply(Math.cos(maxArc)).add(normal(heading).multiply(Math.sin(maxArc))).multiply(groundRadius)
	}

	// ========== Ground-locked drag / zoom ==========

	/**
	 * Pointer direction in camera-local (right, up, forward) coordinates,
	 * from the projection alone (FOV, aspect, lens shift: the inverse of
	 * Perspective.projection). Independent of the camera frame, so it can be
	 * expressed in the frame captured at a gesture's start rather than the
	 * live, spring-lagged one, and never a frame stale the way
	 * Camera.rayFrom (built from the last rendered view matrix) can be
	 */
	private pointerLocalDirection(screenX: number, screenY: number): vec3 | undefined {
		const camera = this.camera
		if (!camera) return undefined
		const perspective = camera.projection as Perspective
		const ndcX = screenX * 2 / perspective.width - 1
		const ndcY = 1 - screenY * 2 / perspective.height
		const f = 1 / Math.tan(perspective.fov * 0.5 * trig.rad)
		const aspect = perspective.width / perspective.height
		return normal(new vec3((ndcX - perspective.offsetX) * aspect / f, (ndcY - perspective.offsetY) / f, 1))
	}

	private static worldDirection(local: vec3, basis: CameraBasis): vec3 {
		return basis.right.multiply(local.x).add(basis.up.multiply(local.y)).add(basis.forward.multiply(local.z))
	}

	/**
	 * Capture the ground under a screen point in the current pose as the
	 * anchor for a ground-locked gesture
	 */
	public captureGroundDragAnchor(screenX: number, screenY: number): GroundDragAnchor | undefined {
		return this.groundAnchorFor(this.getCurrentParams(), screenX, screenY)
	}

	/** The ground under a screen point as seen from an arbitrary pose */
	public groundAnchorFor(params: CameraParams, screenX: number, screenY: number): GroundDragAnchor | undefined {
		const local = this.pointerLocalDirection(screenX, screenY)
		if (!local) return undefined
		const basis = EarthCameraState.basisForParams(params)
		const direction = EarthCameraState.worldDirection(local, basis)
		const hit = new sphere(wgs.a).intersect(new ray(basis.position, direction)) > 0
		const ground = EarthCameraState.cappedGroundPoint(basis.position, direction, cDragRangeAltitudes)
		return { params, basis, ground, hit }
	}

	/**
	 * Pose that puts the anchor's ground point back under the pointer: the
	 * anchor camera, at `radius`, moved about the Earth's centre by the
	 * rotation taking the point now under the pointer onto the grabbed one,
	 * with the anchor's tilt and bearing kept (a north-up view stays
	 * north-up while panning). Exact at any latitude and tilt with no
	 * sensitivity constants.
	 *
	 * Keeping the bearing means the camera frame is not carried rigidly by
	 * the rotation (that would turn it by the meridian convergence), which
	 * shifts the pointer's hit slightly, so the solve is iterated: each pass
	 * is a second-order correction and three are plenty. A different tilt or
	 * bearing may be requested (two-finger twist about the fingers): the hold
	 * then also turns the view about the held point rather than the centre
	 */
	private holdGroundUnderPointer(
		anchor: GroundDragAnchor,
		radius: number,
		local: vec3,
		tilt = anchor.params.tilt,
		bearing = anchor.params.bearing
	): CameraParams {
		let params = new CameraParams(radius, anchor.params.theta, anchor.params.phi, tilt, bearing)
		const unchangedPose = radius === anchor.params.radius && tilt === anchor.params.tilt && bearing === anchor.params.bearing

		for (let pass = 0; pass < 3; pass++) {
			const basis = pass === 0 && unchangedPose ? anchor.basis : EarthCameraState.basisForParams(params)
			const under = EarthCameraState.cappedGroundPoint(basis.position, EarthCameraState.worldDirection(local, basis), cDragRangeAltitudes)
			// (quaternion.fromVec3ToVec3 snaps anything under ~0.8 deg, 90 km
			// on the ground, to identity; drags are far smaller than that)
			const { axis, angle } = rotationBetween(normal(under), normal(anchor.ground))
			if (angle < 1e-9) break
			const position = quaternion.fromAngleAxis(angle, axis).rotateVector(basis.position)
			const sph = new spherical(position)
			params = new CameraParams(radius, sph.theta, sph.phi, tilt, bearing)
		}

		// Keep theta continuous with the running spring so it never takes the
		// long way round the seam
		const current = this.getCurrentParams()
		return new CameraParams(radius, unwrapNear(params.theta, current.theta), params.phi, tilt, bearing)
	}

	/** Pan target: the grabbed ground point under the pointer's new position */
	public dragGroundTo(anchor: GroundDragAnchor, screenX: number, screenY: number): CameraParams | undefined {
		const local = this.pointerLocalDirection(screenX, screenY)
		if (!local) return undefined
		return this.holdGroundUnderPointer(anchor, anchor.params.radius, local)
	}

	/**
	 * Zoom target: the camera at `radius` with the anchor's ground point held
	 * under the screen point, so what is under the cursor / between the
	 * fingers stays put instead of sliding toward the edge. Tilt and bearing
	 * fold toward 0 at the orientation-reset altitudes as moveForward does.
	 * An anchor taken in space (no ground under the pointer) zooms radially
	 * over the anchor's ground point
	 */
	public zoomAboutScreenPoint(anchor: GroundDragAnchor, screenX: number, screenY: number, radius: number): CameraParams | undefined {
		return this.transformAboutScreenPoint(anchor, screenX, screenY, radius, anchor.params.bearing)
	}

	/**
	 * Zoom and turn about a screen point: the camera at `radius` with
	 * `bearing`, and the anchor's ground point held under the screen point.
	 * Pinch, twist and two-finger pan resolve through this in one solve
	 */
	public transformAboutScreenPoint(anchor: GroundDragAnchor, screenX: number, screenY: number, radius: number, bearing: number): CameraParams | undefined {
		const local = this.pointerLocalDirection(screenX, screenY)
		if (!local) return undefined
		const clampedRadius = this.elevationRange.clamp(radius)
		const clippedBearing = this.clipBearing(bearing)
		const { theta, phi, tilt } = anchor.params
		const held = anchor.hit
			? this.holdGroundUnderPointer(anchor, clampedRadius, local, tilt, clippedBearing)
			: new CameraParams(clampedRadius, theta, phi, tilt, clippedBearing)
		// Fold on the wrapped bearing, then unwrap next to the spring so a
		// twist through +-180 degrees doesn't go the long way round
		const folded = this.clipBearingForAltitude(held.bearing, clampedRadius)
		return new CameraParams(
			clampedRadius,
			held.theta,
			held.phi,
			this.clipTiltForAltitude(held.tilt, clampedRadius),
			unwrapNear(folded, this.getCurrentParams().bearing)
		)
	}

	// ========== Fulcrum-centered orbit model ==========

	/**
	 * Capture the current camera as a rigid frame for a fulcrum-centered gesture.
	 * The fulcrum is the point where the camera's forward ray hits the Earth.
	 */
	public captureFulcrumAnchor(): FulcrumOrbitAnchor | undefined {
		const camera = this.camera
		if (!camera) return undefined

		const earthSphere = new sphere(wgs.a + 2)
		const camRay = new ray(camera.position, camera.state.forward)
		const fulcrum = earthSphere.closestPoint(camRay)

		return {
			fulcrum,
			offset: camera.position.subtract(fulcrum),
			forward: camera.state.forward,
			right: camera.state.right,
			up: camera.state.up
		}
	}

	/**
	 * Rotate the camera rigidly about the fulcrum point.
	 *
	 * The motion is a single rotation built from the captured anchor only (no
	 * live-camera reads), so the mapping from gesture input to camera pose is
	 * deterministic and the fulcrum stays fixed on screen:
	 * - rotateDelta orbits the camera around the fulcrum's surface normal (bearing)
	 * - tiltDelta tilts the camera around its right axis (carried by the orbit)
	 *
	 * Both the position offset and the orientation basis are rotated by the same
	 * quaternion, then the resulting look direction is converted back into the
	 * (tilt, bearing) the camera model uses. Returns undefined if there is no
	 * camera to operate on.
	 */
	public orbitAboutFulcrum(anchor: FulcrumOrbitAnchor, rotateDelta: number, tiltDelta: number): CameraParams | undefined {
		const F = anchor.fulcrum
		const n = normal(F)

		// Rotation about n preserves the offset's angle to n, so the tilt limit
		// can be clamped directly from the anchor offset.
		const baseAngle = Math.acos(clamp(dot(n, normal(anchor.offset)), -1, 1))
		let tilt = tiltDelta
		if (baseAngle + tilt <= 0.001) {
			tilt = 0.001 - baseAngle
		} else if (baseAngle + tilt >= trig.half_pi - 0.001) {
			tilt = (trig.half_pi - 0.001) - baseAngle
		}

		const qBearing = quaternion.fromAngleAxis(rotateDelta, n)
		const rightAxis = qBearing.rotateVector(anchor.right)
		const qTilt = quaternion.fromAngleAxis(tilt, rightAxis)
		// Multiply order applies the bearing orbit first, then the tilt.
		const q = qTilt.multiply(qBearing)

		const newPos = q.rotateVector(anchor.offset).add(F)
		const newForward = q.rotateVector(anchor.forward)
		const newUp = q.rotateVector(anchor.up)

		const sph = new spherical(newPos)
		const newRadius = this.elevationRange.clamp(sph.radius)
		const { tilt: newTilt, bearing: newBearing } = this.tiltBearingFromBasis(newPos, newForward, newUp, newRadius)

		return this.createParams(newRadius, sph.theta, sph.phi, newTilt, newBearing)
	}

	/**
	 * Convert a camera basis (position + forward/up) into the model's
	 * (tilt, bearing) parameters. Pure helper used by the fulcrum orbit model.
	 */
	private tiltBearingFromBasis(position: vec3, forward: vec3, up: vec3, radius: number): { tilt: number; bearing: number } {
		const toEarthCenter = normal(position).multiply(-1)

		const tilt = clamp(Math.acos(clamp(dot(forward, toEarthCenter), -1, 1)), 0, this.maxTilt(radius) - 0.001)

		const { right: initialRight, up: initialUp } = EarthCameraState.initialRightUp(toEarthCenter)

		// The right vector is unchanged by tilt (tilt rotates around it), so use
		// it to recover bearing robustly.
		const actualRight = normal(cross(forward, up))
		const rightDotToEarth = dot(actualRight, toEarthCenter)
		const projectedRight = normal(actualRight.subtract(toEarthCenter.multiply(rightDotToEarth)))

		const rightDotInitialRight = clamp(dot(projectedRight, initialRight), -1, 1)
		const rightDotInitialUp = dot(projectedRight, initialUp)
		const bearing = Math.atan2(-rightDotInitialUp, rightDotInitialRight)

		return { tilt, bearing }
	}

	/**
	 * Animate to new camera parameters
	 * If the params include non-zero tilt/bearing different from current reset target,
	 * the orientation reset animation is cancelled (user is manually controlling orientation)
	 */
	public animateTo(params: CameraParams, damping: number = cMovementSpringDamping, source: CameraMotionSource = "auto"): void {
		this.gliding = false
		this.source = source
		this.startSpring(params, damping)
	}

	/**
	 * Carry a released drag on to a projected destination. Same spring as
	 * animateTo, but flagged as a glide so a touch-down can catch it. Always
	 * user motion: a glide is the tail of a drag
	 */
	public animateGlide(params: CameraParams, damping: number): void {
		this.source = "user"
		this.startSpring(params, damping)
		this.gliding = true
	}

	/**
	 * A pointer landing on the globe: stop a glide in flight where it is (as
	 * a finger on a scrolling list does) and persist the pose. Other
	 * motions - fly-tos, crane moves, input tracking - are left alone
	 */
	public catchGlide(): void {
		if (!this.gliding) return
		this.gliding = false
		if (!this.spring.running) return
		this.spring.stop()
		this.onSpringComplete()
	}

	private startSpring(params: CameraParams, damping: number): void {
		const constrained = this.constrainParams(params)
		const currentParams = this.getCurrentParams()
		this.stopCrane()
		// Settle tolerances scale with altitude: the spring stops (without
		// snapping) once inside them, so a fixed 1 km / 0.001 rad left
		// low-altitude moves visibly short of their target (0.001 rad is 6 km
		// of ground) and made a close-in spring complete after a single
		// step. These are about a tenth of a pixel at any altitude
		const altitude = Math.max(constrained.radius - wgs.a, 1)
		const angular = altitude * 2e-8
		this.spring
			.damping(damping)
			.tolerance([altitude * 1e-4, angular, angular, 0.001, 0.001])
			.from(currentParams.array)
			.sendTo(constrained.array)
	}

	/**
	 * Animate to new camera parameters with zoom damping
	 */
	public animateToWithZoomDamping(params: CameraParams): void {
		this.animateTo(params, cZoomSpringDamping)
	}

	/**
	 * Move camera forward by delta distance
	 * When zooming out above the orientation reset altitude, gradually reduces
	 * tilt and bearing toward 0 for a cleaner view of the whole Earth
	 */
	public moveForward(delta: number, source: CameraMotionSource = "auto"): void {
		if (!this.camera) return

		const current = this.getCurrentParams()
		const currentSpherical = new spherical(current.radius, current.theta, current.phi)

		// When we're looking straight at Earth center, zoom should be purely radial.
		// This avoids numerical instability (theta undefined) when x/z are ~0 near the poles.
		const radialTiltEpsilon = 1e-4
		if (Math.abs(current.tilt) < radialTiltEpsilon) {
			const newRadius = this.elevationRange.clamp(currentSpherical.radius - delta)

			// When zooming out, gradually reduce tilt and bearing toward 0
			const newTilt = this.clipTiltForAltitude(current.tilt, newRadius)
			const newBearing = this.clipBearingForAltitude(current.bearing, newRadius)

			const newParams = new CameraParams(
				newRadius,
				currentSpherical.theta,
				currentSpherical.phi,
				newTilt,
				newBearing
			)

			this.animateTo(newParams, cMovementSpringDamping, source)
			return
		}

		// Compute forward from our own params (don’t depend on camera.state vectors)
		const position = currentSpherical.cartesian
		const toEarthCenter = normal(position).multiply(-1)
		const { up: initialUp } = EarthCameraState.initialRightUp(toEarthCenter)

		const bearingQuat = quaternion.fromAngleAxis(current.bearing, toEarthCenter)
		const rotatedUp = bearingQuat.rotateVector(initialUp)
		const rotatedRight = normal(cross(toEarthCenter, rotatedUp))

		const tiltQuat = quaternion.fromAngleAxis(current.tilt, rotatedRight)
		const forward = tiltQuat.rotateVector(toEarthCenter)

		const newPosition = position.add(forward.multiply(delta))
		const newSpherical = new spherical(newPosition)

		// Keep theta/phi continuous to avoid seam jumps during springs
		const wrappedSpherical = currentSpherical.wrappedToClosest(newSpherical)
		const newRadius = this.elevationRange.clamp(newSpherical.radius)

		// When zooming out, gradually reduce tilt and bearing toward 0
		const newTilt = this.clipTiltForAltitude(current.tilt, newRadius)
		const newBearing = this.clipBearingForAltitude(current.bearing, newRadius)

		const newParams = new CameraParams(
			newRadius,
			wrappedSpherical.theta,
			wrappedSpherical.phi,
			newTilt,
			newBearing
		)
		this.animateTo(newParams, cMovementSpringDamping, source)
	}

	/**
	 * Hit test from screen coordinates
	 */
	public hitTestFromScreen(screenX: number, screenY: number): vec3 | undefined {
		if (!this.camera) return undefined

		const camRay = this.camera.rayFrom(screenX, screenY)
		const earthSphere = new sphere(wgs.a)
		const dist = earthSphere.intersect(camRay)

		if (dist > 0) {
			return camRay.pointAt(dist)
		}
		return undefined
	}

	/**
	 * Zoom to a specific point on Earth
	 *
	 * When tilt is 0: Rotates Earth to center the clicked point and zooms halfway,
	 * keeping tilt at 0 for a clean overhead view.
	 *
	 * When tilt > 0: Moves camera halfway toward the hit point and adjusts tilt
	 * to point at it, preserving bearing.
	 */
	public zoomToPoint(hitPoint: vec3): void {
		// Special handling when tilt is 0: rotate to center the point and zoom halfway
		if (this.tilt < 0.01) {
			// Get spherical coordinates of the hit point (on Earth's surface)
			const hitSpherical = new spherical(hitPoint)

			// Calculate new radius: halfway between current altitude and Earth surface
			const currentAltitude = this.spherical.radius - wgs.a
			const newAltitude = currentAltitude * 0.5
			const newRadius = this.elevationRange.clamp(wgs.a + newAltitude)

			// Adjust theta for shortest path
			let theta = hitSpherical.theta
			while (this.spherical.theta - theta > trig.pi) theta += trig.two_pi
			while (this.spherical.theta - theta < -trig.pi) theta -= trig.two_pi

			// Position camera directly above the hit point at the new altitude
			const destParams = new CameraParams(newRadius, theta, hitSpherical.phi, 0, this.bearing)
			this.animateToWithZoomDamping(destParams)
			return
		}

		// Original behavior when tilt > 0
		// Calculate vector from camera to hit point
		const cameraPos = this.spherical.cartesian
		const toHit = hitPoint.subtract(cameraPos)

		// Move half the distance toward the hit point
		const newPos = cameraPos.add(toHit.multiply(0.5))

		// Convert to spherical and clamp altitude
		const newSpherical = new spherical(newPos)
		const radius = this.elevationRange.clamp(newSpherical.radius)

		// Adjust theta for shortest path
		let theta = newSpherical.theta
		while (this.spherical.theta - theta > trig.pi) theta += trig.two_pi
		while (this.spherical.theta - theta < -trig.pi) theta -= trig.two_pi

		// Calculate tilt to point directly at the hit point
		// Nadir direction (toward Earth center) from new position
		const newPosCartesian = new spherical(radius, theta, newSpherical.phi).cartesian
		const toEarthCenter = normal(newPosCartesian).multiply(-1)

		// Direction to the hit point from new position
		const toHitPoint = normal(hitPoint.subtract(newPosCartesian))

		// Tilt is the angle between nadir and the direction to hit point
		const dotProduct = clamp(dot(toEarthCenter, toHitPoint), -1, 1)
		const newTilt = clamp(Math.acos(dotProduct), 0, this.maxTilt(radius) - 0.001)

		// Keep same bearing, use calculated tilt
		const destParams = new CameraParams(radius, theta, newSpherical.phi, newTilt, this.bearing)
		this.animateToWithZoomDamping(destParams)
	}

	/**
	 * Fly to a geographic location, keeping the current altitude unless one
	 * is given (km above the surface)
	 */
	public flyToLocation(lat: number, lon: number, altitude?: number): void {
		const geo = new geographic(lon * trig.rad, lat * trig.rad)
		const sph = new spherical(geo.cartesian)

		const targetRadius = altitude
			? this.elevationRange.clamp(wgs.a + altitude)
			: this.spherical.radius

		// Adjust theta for shortest path
		let theta = sph.theta
		while (this.spherical.theta - theta > trig.pi) theta += trig.two_pi
		while (this.spherical.theta - theta < -trig.pi) theta -= trig.two_pi

		const newParams = new CameraParams(targetRadius, theta, sph.phi, this.tilt, this.bearing)
		this.animateTo(newParams)
	}

	// Altitude thresholds for orientation reset (in meters above Earth surface)
	private static readonly ORIENTATION_RESET_START = 8_000
	private static readonly ORIENTATION_RESET_END = 12_000

	/**
	 * Get maximum tilt for current altitude using geometric horizon calculation
	 *
	 * The maximum tilt is determined by the horizon angle - beyond this, the camera
	 * would be looking into space rather than at Earth. The geometry forms a right
	 * triangle where:
	 *   - Hypotenuse: camera distance from Earth center (R + altitude)
	 *   - Opposite: Earth radius R
	 *   - Angle: maximum tilt from nadir
	 *
	 * Therefore: sin(max_tilt) = R / (R + altitude)
	 *            max_tilt = asin(R / (R + altitude))
	 *
	 * At very high altitudes, gradually reduces to 0 for better viewing experience.
	 */
	public maxTilt(radius?: number): number {
		const r = radius ?? this.spherical.radius
		const altitude = r - wgs.a

		// Geometric horizon limit: max tilt where camera looks at Earth's horizon
		// sin(θ) = R / (R + h), so θ = asin(R / (R + h))
		const horizonTilt = Math.asin(wgs.a / r)

		// Gradually reduce tilt to 0 at very high altitudes for cleaner Earth view
		if (altitude >= EarthCameraState.ORIENTATION_RESET_END) {
			return 0
		} else if (altitude > EarthCameraState.ORIENTATION_RESET_START) {
			const t = (altitude - EarthCameraState.ORIENTATION_RESET_START) /
				(EarthCameraState.ORIENTATION_RESET_END - EarthCameraState.ORIENTATION_RESET_START)
			return horizonTilt * (1 - t)
		}

		return horizonTilt
	}

	/**
	 * Whether the camera is far enough out that the view reads as the whole
	 * globe rather than a place on it: from the altitude where the
	 * orientation reset starts folding tilt and bearing back to 0.
	 */
	public isGlobeView(radius?: number): boolean {
		const r = radius ?? this.spherical.radius
		return r - wgs.a >= EarthCameraState.ORIENTATION_RESET_START
	}

	/**
	 * Clip bearing based on altitude - gradually reduces to 0 at high altitudes
	 */
	private clipBearingForAltitude(bearing: number, radius: number): number {
		const altitude = radius - wgs.a

		if (altitude >= EarthCameraState.ORIENTATION_RESET_END) {
			return 0
		} else if (altitude > EarthCameraState.ORIENTATION_RESET_START) {
			const t = (altitude - EarthCameraState.ORIENTATION_RESET_START) /
				(EarthCameraState.ORIENTATION_RESET_END - EarthCameraState.ORIENTATION_RESET_START)
			return bearing * (1 - t)
		}

		return bearing
	}

	/**
	 * Clip tilt based on altitude - gradually reduces to 0 at high altitudes
	 */
	private clipTiltForAltitude(tilt: number, radius: number): number {
		const altitude = radius - wgs.a
		const maxAllowed = this.maxTilt(radius)

		if (altitude >= EarthCameraState.ORIENTATION_RESET_END) {
			return 0
		} else if (altitude > EarthCameraState.ORIENTATION_RESET_START) {
			const t = (altitude - EarthCameraState.ORIENTATION_RESET_START) /
				(EarthCameraState.ORIENTATION_RESET_END - EarthCameraState.ORIENTATION_RESET_START)
			return Math.min(tilt * (1 - t), maxAllowed)
		}

		return Math.min(tilt, maxAllowed)
	}

	/**
	 * Calculate tilt and bearing from camera vectors
	 * This reverse-engineers the orientation parameters from forward/up vectors
	 */
	private calculateTiltBearingFromVectors(position: vec3, forward: vec3, up: vec3, clampToLimits: boolean = true): void {
		const toEarthCenter = normal(position).multiply(-1)

		// Tilt is the angle between forward and the "looking at Earth center" direction
		const tiltCos = clamp(dot(forward, toEarthCenter), -1, 1)
		this.tilt = Math.acos(tiltCos)

		// Clamp tilt to valid range (unless we're syncing from camera to preserve current state)
		if (clampToLimits) {
			this.tilt = clamp(this.tilt, 0, this.maxTilt(this.spherical.radius) - 0.001)
		}

		// For bearing, create the reference coordinate system (bearing = 0)
		const { right: initialRight, up: initialUp } = EarthCameraState.initialRightUp(toEarthCenter)

		// The camera's right vector is unchanged by tilt rotation (tilt rotates around it)
		// So we use the right vector to calculate bearing, not the up vector
		const actualRight = normal(cross(forward, up))

		// Project onto plane perpendicular to toEarthCenter for robustness
		const rightDotToEarth = dot(actualRight, toEarthCenter)
		const projectedRight = normal(actualRight.subtract(toEarthCenter.multiply(rightDotToEarth)))

		// Calculate bearing as angle between projectedRight and initialRight
		const rightDotInitialRight = clamp(dot(projectedRight, initialRight), -1, 1)
		const rightDotInitialUp = dot(projectedRight, initialUp)

		this.bearing = Math.atan2(-rightDotInitialUp, rightDotInitialRight)
	}

	/**
	 * Clip bearing to [-π, π]
	 */
	public clipBearing(bearing: number): number {
		let b = bearing
		while (b > trig.pi) b -= trig.two_pi
		while (b < -trig.pi) b += trig.two_pi
		return b
	}

	// ========== Sync Methods ==========

	/**
	 * Sync internal state from the current camera position.
	 * Call this when you want to adopt the camera's current position
	 * (e.g., when transitioning from another focus with preserveCurrentState).
	 */
	public syncFromCamera(): void {
		if (!this.camera) return

		// Stop any running animation to prevent interference
		this.stopAnimations()
		this.camera.state.stopAnimating()

		// Calculate spherical from camera position
		this.spherical = new spherical(this.camera.position)

		// Clamp radius to valid range (camera may come from entity focus at a lower altitude)
		const clampedRadius = this.elevationRange.clamp(this.spherical.radius)
		if (clampedRadius !== this.spherical.radius) {
			this.spherical = new spherical(clampedRadius, this.spherical.theta, this.spherical.phi)
		}

		// Calculate tilt AND bearing from camera vectors
		// Don't clamp to limits - we want to preserve the exact current orientation
		this.calculateTiltBearingFromVectors(
			this.spherical.cartesian,
			this.camera.state.forward,
			this.camera.state.up,
			false // Don't clamp - preserve current state exactly
		)
	}

	/**
	 * Called every frame to update camera position
	 */
	public renderUpdate(): void {
		this.updateProjection()
	}

	private updateProjection(): void {
		if (!this.camera || this.camera.state.animating) return

		const position = this.spherical.cartesian
		const elevation = this.spherical.radius - wgs.a

		// Update projection to maximize depth resolution
		const perspective = this.camera.projection as Perspective
		perspective.near = elevation < 30 ? 1 : 10

		perspective.far = position.length + wgs.a

		const state = this.camera.state
		state.position = position

		const toEarthCenter = normal(position).multiply(-1)

		// Create initial coordinate system looking at Earth center
		const { up: initialUp } = EarthCameraState.initialRightUp(toEarthCenter)

		// Apply bearing: rotate the up vector around the toEarthCenter axis
		const bearingQuat = quaternion.fromAngleAxis(this.bearing, toEarthCenter)
		const rotatedUp = bearingQuat.rotateVector(initialUp)
		const rotatedRight = normal(cross(toEarthCenter, rotatedUp))

		// Apply tilt: tilt away from Earth center
		const tiltQuat = quaternion.fromAngleAxis(this.tilt, rotatedRight)
		const forward = tiltQuat.rotateVector(toEarthCenter)
		const up = tiltQuat.rotateVector(rotatedUp)

		state.forward = forward
		state.up = up
	}

	private static initialRightUp(toEarthCenter: vec3): { right: vec3; up: vec3 } {
		// The bearing=0 "up" is the north direction: the world up (unitY) projected
		// onto the plane perpendicular to the view axis. This is unit-length and
		// continuous for every latitude, so panning toward a pole keeps the same
		// roll instead of snapping orientation when nearing the pole.
		//
		// Computing it directly (rather than via cross(toEarthCenter, unitY), whose
		// magnitude collapses to zero at the pole) avoids the abrupt reference-axis
		// switch that previously flipped the view ~8° away from the pole.
		const dotY = dot(toEarthCenter, vec3.unitY)
		const northUp = vec3.unitY.subtract(toEarthCenter.multiply(dotY))

		if (northUp.length < 1e-6) {
			// Exactly over a pole: north is undefined (view axis is parallel to unitY).
			// Fall back to a fixed reference so the frame stays finite and NaN-free.
			const right = normal(cross(toEarthCenter, vec3.unitZ))
			const up = normal(cross(right, toEarthCenter))
			return { right, up }
		}

		const up = normal(northUp)
		const right = normal(cross(toEarthCenter, up))
		return { right, up }
	}

	private onSpringUpdate(values: number[]): void {
		this.spherical = new spherical(values[0], values[1], values[2])
		this.tilt = values[3]
		this.bearing = values[4]
		this.updateProjection()
	}

	private onSpringComplete(): void {
		this.gliding = false
		// Normalize theta to [-π, π]
		let theta = this.spherical.theta
		while (theta > trig.pi) theta -= trig.two_pi
		while (theta < -trig.pi) theta += trig.two_pi

		this.spherical = new spherical(this.spherical.radius, theta, this.spherical.phi)
		this.saveCameraState()
	}
}

